import { makeAutoObservable, runInAction } from 'mobx'
import { handleAxiosError } from '~/shared/lib'
import { KvantsService } from '~/shared/api/services/kvants'
import { FormData, Loaders } from './types'
import {
  AvailabilitesProduct,
  KvantData,
  Kvants,
  ProductOption,
} from '~/shared/api/services/kvants/types'
import { SelectedOption } from '~/shared/api/services/warehouses/types'
import { SelectOption } from '~/shared/ui'

export class KvantsStore {
  loaders: Loaders = {
    kvants: false,
    availabilitiesProduct: false,
    productOptions: false,
    variations: false,
    createNewKvant: false,
    kvantBySlug: false,
    update: false,
    loadMoreKvants: false,
  }

  formData: FormData = {
    name: '',
    shop: null,
    product: '',
    quantity: '',
    vendorCode: '',
    barcode: '',
    description: '',
    weight: '',
    length: '',
    width: '',
    height: '',
  }

  kvantBySlug: KvantData | null = null

  availabilitiesProduct: AvailabilitesProduct[] = []

  selectedOptions: SelectedOption[] = []

  productOptions: ProductOption[] | null = null

  requestSettings = {
    limit: 20,
    offset: 0,
    isAllLoaded: false,
  }

  kvants: Kvants[] = []

  constructor() {
    makeAutoObservable(this)
  }

  activeLoader = (key: keyof Loaders) => {
    this.loaders = { ...this.loaders, [key]: true }
  }

  closeLoader = (key: keyof Loaders) => {
    this.loaders = { ...this.loaders, [key]: false }
  }

  getKvants = async (action?: 'loadMoreKvants') => {
    if (action === 'loadMoreKvants' && this.requestSettings.isAllLoaded) return
    this.activeLoader(action || 'kvants')
    try {
      const params = {
        limit: this.requestSettings.limit,
        offset: action !== 'loadMoreKvants' ? 0 : this.requestSettings.offset,
      }

      const { data } = await KvantsService.getKvants({
        params,
      })

      if (data.length !== 0) {
        if (action === 'loadMoreKvants') {
          runInAction(() => {
            this.kvants = [...this.kvants, ...data]
          })
        } else {
          runInAction(() => {
            this.kvants = data
          })
        }
        this.requestSettings = {
          ...this.requestSettings,
          isAllLoaded: data.length < this.requestSettings.limit,
          offset: action
            ? this.requestSettings.offset + this.requestSettings.limit
            : 20,
        }
      }
    } catch (error) {
      const errorMessage = handleAxiosError(error)
      throw new Error(errorMessage)
    } finally {
      this.closeLoader(action || 'kvants')
    }
  }

  getAvailabilitiesProduct = async (storeId: string, q?: string) => {
    try {
      this.activeLoader('availabilitiesProduct')
      const { data } = await KvantsService.getAvailabilitiesProduct({
        storeId,
        limit: 20,
        ...(q?.trim() ? { q: q.trim() } : {}),
      })
      runInAction(() => {
        this.availabilitiesProduct = data
      })
    } catch (error) {
      const errorMessage = handleAxiosError(error)
      throw new Error(errorMessage)
    } finally {
      runInAction(() => {
        this.closeLoader('availabilitiesProduct')
      })
    }
  }

  getProductOption = async (productId: string) => {
    try {
      this.activeLoader('productOptions')
      const { data } = await KvantsService.getProductOption(productId)
      runInAction(() => {
        this.productOptions = data.options
      })
    } catch (error) {
      const errorMessage = handleAxiosError(error)
      throw new Error(errorMessage)
    } finally {
      runInAction(() => {
        this.closeLoader('productOptions')
      })
    }
  }

  getKvantBySlug = async (id: string) => {
    try {
      this.activeLoader('kvantBySlug')
      const { data } = await KvantsService.getKvantBySlug(id)
      runInAction(() => {
        this.kvantBySlug = data
      })
    } catch (error) {
      const errorMessage = handleAxiosError(error)
      throw new Error(errorMessage)
    } finally {
      runInAction(() => {
        this.closeLoader('kvantBySlug')
      })
    }
  }

  getVariation = async (productId: string) => {
    try {
      this.activeLoader('variations')
      const { data } = await KvantsService.getVariation(productId, {
        params: { optionValues: this.selectedOptions.map((e) => e.value) },
      })
      return data.id
    } catch (error) {
      const errorMessage = handleAxiosError(error)
      throw new Error(errorMessage)
    } finally {
      runInAction(() => {
        this.activeLoader('variations')
      })
    }
  }

  setFormData = (name: keyof FormData, value: SelectOption | string | null) => {
    this.formData[name] = value as SelectOption | (string & SelectOption)
  }

  setSelectedOptions = (obj: SelectedOption) => {
    const existingIndex = this.selectedOptions.findIndex(
      (opt) => opt.optionID === obj.optionID,
    )

    if (existingIndex > -1) {
      this.selectedOptions = this.selectedOptions.map((el, ind) =>
        ind === existingIndex ? obj : el,
      )
    } else {
      this.selectedOptions = [...this.selectedOptions, obj]
    }
  }

  createNewKvant = async (product_variation_id: string) => {
    try {
      this.activeLoader('createNewKvant')
      const store = this.formData.shop as SelectOption | null
      await KvantsService.createNewKvant({
        product_variation_id: product_variation_id || null,
        store_id: store?.value || null,
        name: (this.formData.name as string) || null,
        count: this.formData.quantity ? Number(this.formData.quantity) : null,
        description: (this.formData.description as string) || null,
        vendor_code: (this.formData.vendorCode as string) || null,
        sku: (this.formData.barcode as string) || null,
        width: this.formData.width ? Number(this.formData.width) : null,
        height: this.formData.height ? Number(this.formData.height) : null,
        weight: this.formData.weight ? Number(this.formData.weight) : null,
        length: this.formData.length ? Number(this.formData.length) : null,
        archived_at: null,
        price: null,
        sort_order: null,
      })
    } catch (errors) {
      const errorMessage = handleAxiosError(errors)
      throw new Error(errorMessage)
    } finally {
      this.closeLoader('createNewKvant')
    }
  }

  resetOptions = () => {
    this.selectedOptions = []
  }

  setFormDataEdit = (name: string, value: string) => {
    runInAction(() => {
      if (this.kvantBySlug) {
        this.kvantBySlug = {
          ...this.kvantBySlug,
          [name]: value,
        }
      }
    })
  }

  kvantUpdate = async (id: string) => {
    try {
      this.activeLoader('update')

      const updateKvant = {
        name: this.kvantBySlug?.name || null,
        description: this.kvantBySlug?.description || null,
        vendor_code: this.kvantBySlug?.vendor_code || null,
        sku: this.kvantBySlug?.sku || null,
        width:
          this.kvantBySlug?.width || this.kvantBySlug?.width === 0
            ? Number(this.kvantBySlug.width)
            : null,
        height:
          this.kvantBySlug?.height || this.kvantBySlug?.height === 0
            ? Number(this.kvantBySlug.height)
            : null,
        weight:
          this.kvantBySlug?.weight || this.kvantBySlug?.weight === 0
            ? Number(this.kvantBySlug.weight)
            : null,
        length:
          this.kvantBySlug?.length || this.kvantBySlug?.length === 0
            ? Number(this.kvantBySlug.length)
            : null,
        count:
          this.kvantBySlug?.count || this.kvantBySlug?.count === 0
            ? Number(this.kvantBySlug.count)
            : null,
      }

      await KvantsService.updateKvant(updateKvant, id)
    } catch (errors) {
      const errorMessage = handleAxiosError(errors)
      throw new Error(errorMessage)
    } finally {
      this.closeLoader('update')
    }
  }

  clearFormData = () => {
    this.formData = {
      name: '',
      shop: null,
      product: '',
      quantity: '',
      vendorCode: '',
      description: '',
      barcode: '',
      weight: '',
      length: '',
      width: '',
      height: '',
    }
  }
}

export default KvantsStore
